import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';

/**
 * Where a card's inline form goes on a phone: a sheet with the card's fields and its Cancel and Save
 * pinned at the foot. The card renders it only on a phone and keeps editing in place everywhere else.
 */
export const EditSheet = ({
  open,
  title,
  onCancel,
  onSubmit,
  isPending = false,
  canSave = true,
  error,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onCancel: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isPending?: boolean;
  canSave?: boolean;
  error?: ReactNode;
  children: ReactNode;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={next => !next && !isPending && onCancel()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain sm:max-w-lg">
        {/* `contents`, so the header, fields and footer stay the dialog's own grid rows. */}
        <form onSubmit={onSubmit} className="contents">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="min-w-0">{children}</div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={isPending} onClick={onCancel}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !canSave}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
